import React, { useRef } from 'react';
import { TRANSLATION_LANGUAGES, TranslationLanguage } from '@lingoping/core';
import { NativeLanguageName } from './TranslationText.js';

interface Props {
  value: TranslationLanguage;
  onChange: (language: TranslationLanguage) => void;
}

/**
 * The 3x3 language grid of the first onboarding step: a radio group (one tab stop, arrow keys move
 * and select, like native radio buttons), each option showing the language's own name and its
 * English name.
 */
export const TranslationLanguagePicker: React.FC<Props> = ({ value, onChange }) => {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const count = TRANSLATION_LANGUAGES.length;

  const move = (from: number, step: number) => {
    const next = (from + step + count) % count;
    onChange(TRANSLATION_LANGUAGES[next].code);
    refs.current[next]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') move(index, 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') move(index, -1);
    else if (e.key === 'Home') move(index, -index);
    else if (e.key === 'End') move(index, count - 1 - index);
    else return;
    e.preventDefault();
  };

  return (
    <div
      role="radiogroup"
      aria-label="Translation language"
      style={{
        display: 'grid',
        // Three columns in the onboarding card; two when the screen is too narrow for the names.
        gridTemplateColumns: 'repeat(auto-fit, minmax(max(120px, 28%), 1fr))',
        gap: 10,
      }}
    >
      {TRANSLATION_LANGUAGES.map((info, index) => {
        const checked = info.code === value;
        return (
          <button
            key={info.code}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(info.code)}
            onKeyDown={(e) => onKeyDown(e, index)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 2,
              minHeight: 58,
              padding: '10px 14px',
              borderRadius: 10,
              cursor: 'pointer',
              textAlign: 'start',
              fontFamily: 'inherit',
              color: '#0f172a',
              background: checked ? '#e6f4ff' : '#ffffff',
              border: `2px solid ${checked ? '#1677ff' : '#e5e7eb'}`,
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              <NativeLanguageName lang={info.code} />
            </span>
            <span style={{ fontSize: 12, color: '#5b6472' }}>{info.name}</span>
          </button>
        );
      })}
    </div>
  );
};
